/**
 * Brand-aware native search.
 *
 * Shopify indexes product titles, and every title here names the scent but not the house:
 * "BEY145 - Inspired By Donna Born In Roma" contains no "Valentino". Two things follow, and this
 * element solves the second:
 *
 *   1. A brand has to exist somewhere Shopify indexes. That is the product-tag import
 *      (scripts/build-tag-import.mjs) — data, not code.
 *   2. Abbreviations still find nothing, because no tag says "LV". Expanding them is this file.
 *
 * `expandQuery` is exported because the header typeahead needs the same treatment:
 * `assets/predictive-search.js` runs it on every keystroke so the dropdown answers "LV" the way the
 * results page does. There the input is left alone and only the fetched query is expanded — the
 * shopper should still see what they typed while they are typing it.
 *
 * On submit the input's value itself is rewritten, before the browser navigates. That is deliberate
 * and the alternatives are worse: `assets/facets.js` reads `q` straight from
 * `window.location` and re-injects it into every filter request, so if `q` stayed "lv" while
 * results were produced some other way, the grid would snap back to nothing the first time a
 * shopper ticked a filter. There must be exactly one value in `q`, and it must be the one that
 * actually produced what is on screen.
 *
 * No preventDefault, no fetch, no history manipulation: the form submits normally, so the back
 * button, bookmarking and JS-off all behave as they always did. With JS off the literal term is
 * searched, finds nothing, and the search page's empty state offers brand chips and the finder.
 *
 * The tables come from the Dupe Finder, which already curates them.
 */

import { normalise } from '@theme/pb-comparator-search';
import { ALIASES, expandAliases } from '@theme/pb-comparator-brands';

/**
 * Expand a raw query, or return it unchanged when nothing matched.
 *
 * The whole query is tried first so multi-word keys ("de marly") beat their parts, and so a hit
 * returns the table's own spelling — "Louis Vuitton", not the normalised "louis vuitton" that a
 * per-word pass produces. Only then does it fall back to expanding individual words, which is what
 * makes "lv imagination" work as well as "lv".
 *
 * @param {string} raw
 * @returns {string} the expanded query, or `raw` untouched
 */
export function expandQuery(raw) {
  const key = normalise(raw);
  if (!key) return raw;

  const whole = ALIASES[key];
  if (whole) return whole;

  const expanded = expandAliases(raw, normalise);
  return expanded && expanded !== key ? expanded : raw;
}

class SearchAlias extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form') || this.closest('form');
    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const input = form.querySelector('input[name="q"]');
    if (!(input instanceof HTMLInputElement)) return;

    const typed = input.value.trim();
    if (!typed) return;

    const expanded = expandQuery(typed);
    if (expanded === typed) return;

    // Nothing else is carried through. The results page echoes the query it actually ran, so the
    // search box and the summary both read "Louis Vuitton" — which explains the expansion without a
    // second parameter that `facets.js` would drop on the first filter change anyway.
    input.value = expanded;
  };
}

if (!customElements.get('search-alias')) {
  customElements.define('search-alias', SearchAlias);
}
